// Chakra imports
import { Badge, Box, Flex, IconButton, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';

import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaBan, FaCheck, FaClock, FaExclamationTriangle, FaEye, FaTimes, FaUndo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Column } from 'react-table';
import { findAllMovements } from 'services/api.service';
import { MovementsType, MovementsTypeDescription } from 'types/Movement';
import { maskCurrency } from 'utils/number';

export enum MovementsStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  CHARGEBACK = 'CHARGEBACK',
}

export const MovementsStatusDescription: Record<MovementsStatus, string> = {
  [MovementsStatus.PENDING]: 'Pendente',
  [MovementsStatus.APPROVED]: 'Aprovada',
  [MovementsStatus.REJECTED]: 'Recusada',
  [MovementsStatus.CANCELED]: 'Cancelada',
  [MovementsStatus.REFUNDED]: 'Estornada',
  [MovementsStatus.PARTIALLY_REFUNDED]: 'Estornada em parte',
  [MovementsStatus.CHARGEBACK]: 'Chargeback',
};

const STATUS_STYLE: Record<MovementsStatus, { colorScheme: string; icon: JSX.Element }> = {
  [MovementsStatus.PENDING]: { colorScheme: 'orange', icon: <FaClock /> },
  [MovementsStatus.APPROVED]: { colorScheme: 'green', icon: <FaCheck /> },
  [MovementsStatus.REJECTED]: { colorScheme: 'red', icon: <FaTimes /> },
  [MovementsStatus.CANCELED]: { colorScheme: 'gray', icon: <FaBan /> },
  [MovementsStatus.REFUNDED]: { colorScheme: 'orange', icon: <FaUndo /> },
  [MovementsStatus.PARTIALLY_REFUNDED]: { colorScheme: 'yellow', icon: <FaUndo /> },
  [MovementsStatus.CHARGEBACK]: { colorScheme: 'purple', icon: <FaExclamationTriangle /> },
};

/**
 * Quatro status desenhavam o mesmo ✗ e só se distinguiam pela cor — estornada, estornada em
 * parte, recusada e cancelada. Na listagem o tooltip desempata; na tela de detalhe o rótulo
 * vem escrito, que é onde a pergunta "o que aconteceu com essa venda" de fato se faz.
 */
export const StatusRender = (status: MovementsStatus, options?: { withLabel?: boolean }) => {
  const style = STATUS_STYLE[status];
  if (!style) return null;

  const label = MovementsStatusDescription[status] ?? status;

  if (options?.withLabel)
    return (
      <Badge colorScheme={style.colorScheme} display="inline-flex" alignItems="center" gap={2} px={2} py={1}>
        {style.icon}
        {label}
      </Badge>
    );

  return (
    <Tooltip label={label} placement="top" hasArrow>
      <Badge colorScheme={style.colorScheme}>{style.icon}</Badge>
    </Tooltip>
  );
};
export default function Orders() {
  const navigate = useNavigate();
  const columns = useMemo(
    () =>
      [
        {
          Header: 'Tipo',
          filter: true,
          values: Object.values(MovementsType).map((type) => ({
            value: type,
            label: MovementsTypeDescription[type],
          })),
          accessor: 'type',
          Cell: ({ value }) => {
            return (
              <Text
                noOfLines={1}
                fontSize="sm"
              >
                {MovementsTypeDescription[value]}
              </Text>
            );
          },
        },
        {
          Header: 'Status',
          accessor: 'status',
          filter: true,
          values: [
            {
              value: MovementsStatus.PENDING,
              label: 'Pendente',
            },
            {
              value: MovementsStatus.APPROVED,
              label: 'Aprovado',
            },
            {
              value: MovementsStatus.REJECTED,
              label: 'Rejeitado',
            },
            {
              value: MovementsStatus.CANCELED,
              label: 'Cancelado',
            },
          ],
          Cell: ({ value }: { value: MovementsStatus }) => {
            return StatusRender(value);
          },
        },
        {
          Header: 'Bruto',
          accessor: 'amount',
          numeric: true,
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },

        {
          Header: 'Líquido',
          numeric: true,
          accessor: 'amountLiquid',
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },
        {
          Header: 'Parcelas',
          numeric: true,
          accessor: 'installmentCount',
          Cell: ({ value }) => {
            return value === 1 ? 'À vista' : `${value}x`;
          },
        },
        {
          accessor: 'orderAt',
          Header: 'Data',
          Cell: ({ value }) => {
            return DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss');
          },
        },

        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: any) => (
    <>
      <Flex
        align="center"
        justify="flex-start"
        w="100%"
        display={{ base: 'none', md: 'flex' }}
      >
        <IconButton
          aria-label="Apertar"
          size="sm"
          variant="outline"
          icon={<FaEye />}
          onClick={() => navigate(`/access/movement/${row.id}`)}
        />
      </Flex>
      <Flex
        align="center"
        justify="flex-start"
        w="100%"
        display={{ base: 'flex', md: 'none' }}
      >
        <CustomButton
          variant="solid"
          colorScheme="brand"
          onClick={() => navigate(`/access/movement/${row.id}`)}
        >
          Acessar
        </CustomButton>
      </Flex>
    </>
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['findAllMovements'] });
  };

  // Chakra Color Mode
  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        <SimpleGrid
          mb="20px"
          columns={{ sm: 1 }}
          spacing={{ base: '20px', xl: '20px' }}
        >
          <TableComponent
            columnsData={columns}
            filterable={['customerId', 'maquinetaId', 'createdAt', 'promoterId', 'paymentType', 'installments', 'flagCard']}
            queryFn={findAllMovements}
            exportPdf={true}
            exportCsv={true}
            queryKey="findAllMovements"
            actions={actions}
            label="Movimentações de vendas"
          />
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
